import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './Affiliates.css';

interface Affiliate {
  id: number;
  name: string;
  email: string;
  api_key: string;
  country: string;
  callback_template: string;
  status: 'pending' | 'approved' | 'blocked';
  timezone: string;
  tier: 'standard' | 'premium' | 'sensitive';
  postback_percentage: number;
  daily_cap?: number;
  monthly_cap?: number;
  created_at: string;
}

const Affiliates: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchAffiliates();
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPublishers({
        limit: itemsPerPage,
        page: currentPage
      });
      setAffiliates(response.publishers);
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
        setTotalCount(response.pagination.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch publishers');
    } finally {
      setLoading(false);
    }
  };

  const filteredAffiliates = affiliates.filter(affiliate =>
    affiliate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    affiliate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    affiliate.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleStatus = async (id: number) => {
    const affiliate = affiliates.find(a => a.id === id);
    if (!affiliate) return;

    let newStatus: 'pending' | 'approved' | 'blocked';
    switch (affiliate.status) {
      case 'pending':
        newStatus = 'approved';
        break;
      case 'approved':
        newStatus = 'blocked';
        break;
      case 'blocked':
        newStatus = 'pending';
        break;
      default:
        newStatus = 'pending';
    }

    try {
      await apiService.updatePublisher(id, { status: newStatus });
      setAffiliates(affiliates.map(aff =>
        aff.id === id ? { ...aff, status: newStatus } : aff
      ));
    } catch (err: any) {
      alert(`Failed to update publisher status: ${err.message}`);
    }
  };

  const handleView = (id: number) => {
    navigate(`/affiliates/view/${id}`);
  };

  const handleEdit = (id: number) => {
    navigate(`/affiliates/edit/${id}`);
  };

  const handleAddNew = () => {
    navigate('/affiliates/add');
  };

  const handleLogin = (id: number, name: string) => {
    // Navigate to publisher view with admin impersonation
    navigate(`/publisher-view/${id}`, { state: { publisherName: name, isAdminView: true } });
  };

  return (
    <div className="affiliates-page">
      <div className="page-header">
        <h1>All Affiliate</h1>
        <button className="btn btn-primary" onClick={handleAddNew}>
          <span className="btn-icon">➕</span>
          Add Affiliate
        </button>
      </div>

      <div className="affiliates-section">
        <div className="section-header">
          <div className="export-buttons">
            <button className="btn btn-success">Copy</button>
            <button className="btn btn-info">CSV</button>
            <button className="btn btn-warning">Excel</button>
            <button className="btn btn-danger">PDF</button>
            <button className="btn btn-secondary">Print</button>
          </div>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container">
          <table className="affiliates-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>API Key</th>
                <th>Country</th>
                <th>Timezone</th>
                <th>Tier</th>
                <th>Postback %</th>
                <th>Daily Cap</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '20px' }}>
                    Loading affiliates...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '20px', color: '#e74c3c' }}>
                    Error: {error}
                  </td>
                </tr>
              ) : filteredAffiliates.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '20px' }}>
                    No affiliates found
                  </td>
                </tr>
              ) : (
                filteredAffiliates.map((affiliate) => (
                  <tr key={affiliate.id}>
                    <td>{affiliate.id}</td>
                    <td>{affiliate.name}</td>
                    <td>{affiliate.email}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '11px', maxWidth: '150px' }}>
                      {affiliate.api_key}
                    </td>
                    <td>{affiliate.country}</td>
                    <td style={{ fontSize: '12px' }}>{affiliate.timezone}</td>
                    <td>
                      {affiliate.tier ? (
                        <span className={`tier-badge ${affiliate.tier}`}>
                          {affiliate.tier.charAt(0).toUpperCase() + affiliate.tier.slice(1)}
                        </span>
                      ) : (
                        <span className="tier-badge standard">Standard</span>
                      )}
                    </td>
                    <td>
                      <span className="postback-percent">{affiliate.postback_percentage ?? 100}%</span>
                    </td>
                    <td>{affiliate.daily_cap != null ? affiliate.daily_cap.toLocaleString() : 'Unlimited'}</td>
                    <td>
                      <button
                        className={`status-badge ${affiliate.status}`}
                        onClick={() => toggleStatus(affiliate.id)}
                      >
                        {affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1)}
                        <span className="status-toggle">Change Status</span>
                      </button>
                    </td>
                    <td>
                      <div className="action-links">
                        <button
                          type="button"
                          className="action-link"
                          onClick={() => handleEdit(affiliate.id)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="action-link"
                          onClick={() => handleView(affiliate.id)}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="action-link"
                          onClick={() => handleLogin(affiliate.id, affiliate.name)}
                          title="View as Publisher"
                        >
                          Login
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="table-info">
            Showing {filteredAffiliates.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
          </div>
          <div className="pagination">
            <button
              className="page-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="page-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Affiliates;
